import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Typography, Tag, Button, Popconfirm, message } from 'antd';
import dayjs from 'dayjs';
import CrudPage from '../../../components/common/CrudPage';
import { configService } from '../../../services/next';
import { CopyOutlined } from '@ant-design/icons';

const { Text } = Typography;

const STATUS_OPTIONS = [
  { label: 'Hiển thị', value: 1 },
  { label: 'Ẩn', value: 0 },
];

const TARGET_TYPES = [
  { label: 'Platform', value: 1 },
  { label: 'Version', value: 2 },
  { label: 'Topic', value: 3 },
  { label: 'Audience', value: 8 },
  { label: 'Not in Audience', value: 9 },
];

const QuanLyCauHinh = () => {
  const { data: metaRes } = useQuery({
    queryKey: ['configAudiences'],
    queryFn: async () => {
      const res = await configService.getAudiences();
      return res?.data?.data || {};
    }
  });

  const audienceOptions = useMemo(() => {
    return (metaRes?.audiences || []).map(a => ({ label: a.name, value: a.code }));
  }, [metaRes]);

  const topicOptions = useMemo(() => metaRes?.topics?.topic || [], [metaRes]);
  const platformOptions = useMemo(() => metaRes?.topics?.platform || [], [metaRes]);
  
  const handleCopy = async (id, actionRef) => {
      try {
          const res = await configService.copy(id);
          if (res && res.code === 200) {
              message.success('Sao chép thành công');
              if (actionRef && actionRef.current) {
                  actionRef.current.reload();
              }
          } else {
              message.error(res?.message || 'Sao chép thất bại');
          }
      } catch (err) {
          message.error('Lỗi sao chép');
      }
  };

  const columns = useMemo(() => [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'KEY', dataIndex: 'key', key: 'key', width: 200, render: (t) => <Text strong>{t}</Text> },
    { title: 'APP ID', dataIndex: 'app_id', key: 'app_id', width: 100 },
    { title: 'GIÁ TRỊ', dataIndex: 'value', key: 'value', width: 250, ellipsis: true },
    { title: 'LOẠI', dataIndex: 'type', key: 'type', width: 100 },
    { title: 'VERSION', dataIndex: 'config_version', key: 'config_version', width: 100 },
    { 
        title: 'TRẠNG THÁI', 
        dataIndex: 'status', 
        key: 'status', 
        width: 110, 
        render: (s) => { 
            const map = { 0: ['default','ẨN'], 1: ['green','HIỂN THỊ'] }; 
            const [c,l] = map[s] || ['default', s]; 
            return <Tag color={c}>{l}</Tag>; 
        } 
    },
    { title: 'NGÀY BẮT ĐẦU', dataIndex: 'start_time', key: 'start_time', width: 150, render: (t) => t ? dayjs(t).format('DD/MM/YYYY HH:mm') : '-' },
    { title: 'NGÀY KẾT THÚC', dataIndex: 'end_time', key: 'end_time', width: 150, render: (t) => t ? dayjs(t).format('DD/MM/YYYY HH:mm') : '-' },
    { title: 'NGÀY TẠO', dataIndex: 'create_time', key: 'create_time', width: 150, render: (t) => t ? dayjs(t).format('DD/MM/YYYY HH:mm') : '-' },
  ], []);

  const formFields = useMemo(() => [
    { name: 'key', label: 'Key cấu hình', type: 'input', rules: [{ required: true, message: 'Vui lòng nhập key!' }] },
    { name: 'app_id', label: 'App ID', type: 'input' },
    { name: 'type', label: 'Loại dữ liệu', type: 'select', rules: [{ required: true, message: 'Vui lòng chọn loại dữ liệu!' }], options: [
      { label: 'String', value: 'string' },
      { label: 'Number', value: 'number' },
      { label: 'DateTime', value: 'datetime' },
      { label: 'Html', value: 'html' },
      { label: 'Json', value: 'json' },
    ] },
    { name: 'config_version', label: 'Version áp dụng', type: 'input', tooltip: 'Lưu ý: Điền version để áp dụng cấu hình này' },
    { name: 'description', label: 'Mô tả', type: 'textarea', propsInput: { rows: 2 } },
    { name: 'value', label: 'Giá trị (Value)', type: 'textarea', rules: [{ required: true, message: 'Vui lòng nhập giá trị!' }], propsInput: { rows: 5 } },
    { name: 'start_time', label: 'Ngày bắt đầu', type: 'date', format: 'DD/MM/YYYY HH:mm:ss', propsInput: { showTime: true, format: 'DD/MM/YYYY HH:mm:ss' } },
    { name: 'end_time', label: 'Ngày kết thúc', type: 'date', format: 'DD/MM/YYYY HH:mm:ss', propsInput: { showTime: true, format: 'DD/MM/YYYY HH:mm:ss' } },
    { name: 'status', label: 'Trạng thái', type: 'select', options: STATUS_OPTIONS, defaultValue: 1 },
    { name: 'platform', label: 'Platform', type: 'select', options: platformOptions, propsInput: { mode: 'tags', placeholder: 'Chọn hoặc nhập Platform', optionFilterProp: 'label' } },
    { name: 'topic', label: 'Topic', type: 'select', options: topicOptions, propsInput: { mode: 'tags', placeholder: 'Chọn hoặc nhập Topic', optionFilterProp: 'label' } },
    { name: 'audience', label: 'Audience', type: 'select', propsInput: { mode: 'multiple', placeholder: 'Chọn Audience', optionFilterProp: 'label' }, options: audienceOptions },
    { name: 'not_in_audience', label: 'Not in Audience', type: 'select', propsInput: { mode: 'multiple', placeholder: 'Chọn Audience', optionFilterProp: 'label' }, options: audienceOptions },
    { 
        name: 'targets', 
        label: '', 
        type: 'formList',
        listProps: {
            addLabel: 'Thêm Target',
            fields: [
                { name: 'type', label: 'Loại Target', type: 'select', options: TARGET_TYPES, span: 8, rules: [{ required: true, message: 'Chọn loại!' }] },
                { name: 'target', label: 'Giá trị Target', type: 'input', span: 14, rules: [{ required: true, message: 'Nhập giá trị!' }] }
            ]
        }
    }
  ], [audienceOptions, topicOptions, platformOptions]);

  const filterFields = useMemo(() => [
    {
      label: 'Trạng thái',
      value: 'status',
      options: [
        { label: 'Tất cả', value: '' },
        ...STATUS_OPTIONS,
      ],
    },
  ], []);

  const customActions = (record, actionRef) => (
    <Popconfirm
      title="Sao chép cấu hình này?"
      onConfirm={() => handleCopy(record.id, actionRef)}
      okText="Có"
      cancelText="Không"
    >
      <Button type="default" icon={<CopyOutlined />} size="small">
        Copy
      </Button>
    </Popconfirm>
  );

  return (
    <CrudPage
      title="QUẢN LÝ CẤU HÌNH"
      subTitle="Quản lý cấu hình động và targeting"
      itemsName="Cấu hình"
      queryKey="configList"
      service={configService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      searchPlaceholder="Tìm kiếm theo key..."
      addButtonLabel="Thêm cấu hình"
      formConfig={{ width: 1000 }}
      customActions={customActions}
      mapFormToData={(payload) => {
        let targets = payload.targets || [];
        const finalTargets = targets.filter(t => ![1, 3, 8, 9].includes(t.type));
        if (payload.platform) {
          payload.platform.forEach(item => finalTargets.push({ type: 1, target: item }));
        }
        if (payload.topic) {
          payload.topic.forEach(item => finalTargets.push({ type: 3, target: item }));
        }
        if (payload.audience) {
          payload.audience.forEach(item => finalTargets.push({ type: 8, target: item }));
        }
        if (payload.not_in_audience) {
          payload.not_in_audience.forEach(item => finalTargets.push({ type: 9, target: item }));
        }
        return { targets: finalTargets, audience: undefined, not_in_audience: undefined, platform: undefined, topic: undefined };
      }}
      mapDataToForm={(record) => {
        const targets = record.targets || [];
        const platform = targets.filter(t => t.type === 1).map(t => t.target);
        const topic = targets.filter(t => t.type === 3).map(t => t.target);
        const audience = targets.filter(t => t.type === 8).map(t => t.target);
        const not_in_audience = targets.filter(t => t.type === 9).map(t => t.target);
        const otherTargets = targets.filter(t => ![1, 3, 8, 9].includes(t.type));
        return { platform, topic, audience, not_in_audience, targets: otherTargets };
      }}
    />
  );
};

export default QuanLyCauHinh;
